/**
 * Shared FFmpeg.wasm loader (CDN scripts — CRA 3.4 cannot transpile @ffmpeg 0.12).
 * Needs COOP/COEP for SharedArrayBuffer — see public/.htaccess + serve-isolated.js.
 */

const FFMPEG_SCRIPT = 'https://cdn.jsdelivr.net/npm/@ffmpeg/ffmpeg@0.11.6/dist/ffmpeg.min.js';
const CORE_PATH = 'https://cdn.jsdelivr.net/npm/@ffmpeg/core@0.11.0/dist/ffmpeg-core.js';

let ffmpegInstance: any = null;
let loadPromise: Promise<any> | null = null;
let scriptPromise: Promise<void> | null = null;

export function getWin(): any {
  const g: any = typeof global !== 'undefined' ? global : {};
  return g.window || g;
}

export function hasSharedArrayBuffer(): boolean {
  const win = getWin();
  return typeof win.SharedArrayBuffer !== 'undefined';
}

export function extFromName(name: string, fallback: string): string {
  const m = /\.([a-z0-9]+)$/i.exec(name || '');
  return m ? m[1].toLowerCase() : fallback;
}

export function revokeUrl(url: string | null) {
  if (!url) return;
  try {
    const win = getWin();
    if (win.URL && win.URL.revokeObjectURL) win.URL.revokeObjectURL(url);
  } catch {
    /* ignore */
  }
}

export function resetFFmpeg() {
  ffmpegInstance = null;
  loadPromise = null;
}

function loadScript(src: string): Promise<void> {
  const win = getWin();
  const doc = win.document;
  return new Promise((resolve, reject) => {
    const existing = doc.querySelector(`script[data-l123-ffmpeg="${src}"]`);
    if (existing) {
      resolve();
      return;
    }
    const s = doc.createElement('script');
    s.src = src;
    s.async = true;
    s.crossOrigin = 'anonymous';
    s.setAttribute('data-l123-ffmpeg', src);
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`Không tải được script: ${src}`));
    doc.head.appendChild(s);
  });
}

export async function ensureScript(): Promise<any> {
  const win = getWin();
  if (win.FFmpeg && win.FFmpeg.createFFmpeg) return win.FFmpeg;
  if (!scriptPromise) {
    scriptPromise = loadScript(FFMPEG_SCRIPT).catch((e) => {
      scriptPromise = null;
      throw e;
    });
  }
  await scriptPromise;
  if (!win.FFmpeg || !win.FFmpeg.createFFmpeg) {
    throw new Error('Thư viện FFmpeg không khởi tạo được.');
  }
  return win.FFmpeg;
}

export async function ensureFFmpeg(onStatus?: (msg: string) => void): Promise<any> {
  if (!hasSharedArrayBuffer()) {
    throw new Error(
      'Trình duyệt chưa bật SharedArrayBuffer. Hãy mở trực tiếp link công cụ (tải lại trang đầy đủ), không chỉ chuyển từ menu nội bộ. Nếu vẫn lỗi, thử Chrome/Edge bản mới.'
    );
  }

  if (ffmpegInstance && ffmpegInstance.isLoaded && ffmpegInstance.isLoaded()) {
    return ffmpegInstance;
  }
  if (loadPromise) return loadPromise;

  loadPromise = (async () => {
    onStatus && onStatus('Đang tải FFmpeg (~25MB, lần đầu)…');
    const FFmpeg = await ensureScript();
    const ffmpeg = FFmpeg.createFFmpeg({
      log: false,
      corePath: CORE_PATH,
      progress: ({ ratio }: any) => {
        if (ffmpeg.__onProgress && typeof ratio === 'number') {
          ffmpeg.__onProgress(Math.max(0, Math.min(1, ratio)));
        }
      },
    });
    await ffmpeg.load();
    ffmpegInstance = ffmpeg;
    onStatus && onStatus('FFmpeg sẵn sàng.');
    return ffmpeg;
  })();

  try {
    return await loadPromise;
  } catch (e) {
    loadPromise = null;
    throw e;
  }
}

export type FFmpegRunOptions = {
  onProgress?: (ratio: number) => void;
  signal?: { aborted: boolean };
  onStatus?: (msg: string) => void;
};

/**
 * Write input, run ffmpeg args, read output file as Blob. Cleans up FS entries.
 */
export async function runFFmpegJob(
  inputFile: any,
  inputName: string,
  outputName: string,
  args: string[],
  mimeType: string,
  opts: FFmpegRunOptions = {}
): Promise<any> {
  const ffmpeg = await ensureFFmpeg(opts.onStatus);
  const FFmpeg = await ensureScript();
  const fetchFile = FFmpeg.fetchFile;

  ffmpeg.__onProgress = (ratio: number) => {
    if (opts.onProgress) opts.onProgress(ratio);
  };

  ffmpeg.FS('writeFile', inputName, await fetchFile(inputFile));

  if (opts.signal && opts.signal.aborted) {
    throw new Error('Đã hủy.');
  }

  const runPromise = ffmpeg.run(...args);

  await new Promise<void>((resolve, reject) => {
    const id = setInterval(() => {
      if (opts.signal && opts.signal.aborted) {
        clearInterval(id);
        try {
          if (ffmpeg.exit) ffmpeg.exit();
        } catch {
          /* ignore */
        }
        resetFFmpeg();
        reject(new Error('Đã hủy.'));
      }
    }, 400);
    runPromise.then(
      () => {
        clearInterval(id);
        resolve();
      },
      (err: any) => {
        clearInterval(id);
        reject(err);
      }
    );
  });

  const data = ffmpeg.FS('readFile', outputName);
  try {
    ffmpeg.FS('unlink', inputName);
    ffmpeg.FS('unlink', outputName);
  } catch {
    /* ignore */
  }
  ffmpeg.__onProgress = null;

  const win = getWin();
  const BlobCtor = win.Blob || (global as any).Blob;
  const buf = data.buffer ? data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength) : data;
  return new BlobCtor([buf], { type: mimeType });
}

export function downloadBlob(blob: any, filename: string) {
  const win = getWin();
  const url = win.URL.createObjectURL(blob);
  const a = win.document.createElement('a');
  a.href = url;
  a.download = filename;
  win.document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => revokeUrl(url), 1000);
}

export function replaceExt(name: string, newExt: string): string {
  const base = (name || 'file').replace(/\.[^.]+$/, '');
  return `${base}.${newExt.replace(/^\./, '')}`;
}
